import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Users, Compass, Link2, Zap } from 'lucide-react'

const NextSteps = () => {
  const columns = [
    {
      id: 'explore',
      title: 'EXPLORE',
      icon: Compass,
      color: 'primary',
      image: 'https://images.unsplash.com/photo-1529070538774-1843cb3265df?w=800&auto=format&fit=crop&q=80',
      sections: [
        {
          title: 'MEET & GREET',
          description: 'Come meet the pastor and learn more about who we are. This casual gathering is the perfect opportunity to ask questions and find out how you can get connected.',
          buttonText: 'DATES & INFO',
          buttonLink: '/plan-visit',
        }
      ]
    },
    {
      id: 'connect',
      title: 'CONNECT',
      icon: Link2,
      color: 'secondary',
      image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&auto=format&fit=crop&q=80',
      sections: [
        {
          title: 'JOIN A GROUP',
          description: 'Have you given your life to Christ, but need an environment where you can grow and develop in your faith? Join any of our small groups to build your faith in the Lord. Our groups meet throughout the week in various locations, providing fellowship, Bible study, and authentic community.',
          buttonText: 'LEARN MORE',
          buttonLink: '/ministries',
        }
      ]
    },
    {
      id: 'engage',
      title: 'ENGAGE',
      icon: Zap,
      color: 'accent',
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&auto=format&fit=crop&q=80',
      sections: [
        {
          title: 'SERVE',
          description: 'Discover your gifts and find your place to serve. From hospitality to worship, children\'s ministry to outreach, there\'s a place for you to make a difference.',
          buttonText: 'MORE INFO',
          buttonLink: '/ministries',
        }
      ]
    }
  ]

  const colorClasses = {
    primary: {
      gradient: 'from-primary-500 to-primary-600',
      bg: 'bg-primary-500',
      bgHover: 'hover:bg-primary-600',
      text: 'text-primary-500',
      underline: 'bg-primary-500',
      overlay: 'from-primary-900/80 to-primary-800/60',
      light: 'bg-primary-50',
    },
    secondary: {
      gradient: 'from-secondary-500 to-secondary-600',
      bg: 'bg-secondary-500',
      bgHover: 'hover:bg-secondary-600',
      text: 'text-secondary-500',
      underline: 'bg-secondary-500',
      overlay: 'from-secondary-900/80 to-secondary-800/60',
      light: 'bg-secondary-50',
    },
    accent: {
      gradient: 'from-accent-500 to-accent-600',
      bg: 'bg-accent-500',
      bgHover: 'hover:bg-accent-600',
      text: 'text-accent-500',
      underline: 'bg-accent-500',
      overlay: 'from-accent-900/80 to-accent-800/60',
      light: 'bg-accent-50',
    }
  }

  return (
    <section className="py-24 bg-gradient-to-b from-white via-light-100 to-white relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-secondary-200/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-accent-200/20 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary-100 to-secondary-100 text-primary-700 text-sm font-semibold mb-4"
            whileHover={{ scale: 1.05 }}
          >
            Your Journey Starts Here
          </motion.span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-dark-100 mb-6">
            Next <span className="gradient-text">Steps</span>
          </h2>
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="text-lg md:text-xl font-accent font-semibold text-primary-500">EXPLORE</span>
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500" />
            <span className="text-lg md:text-xl font-accent font-semibold text-secondary-500">CONNECT</span>
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-secondary-500 to-accent-500" />
            <span className="text-lg md:text-xl font-accent font-semibold text-accent-500">ENGAGE</span>
          </div>
          <p className="text-lg text-dark-300 max-w-3xl mx-auto font-body leading-relaxed">
            At DLBC Clarksville, we are committed to making disciples who know God,
            grow in faith, and go out to serve others. Here's how you can take your next step.
          </p>
        </motion.div>

        {/* Three Columns */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {columns.map((column, index) => {
            const colors = colorClasses[column.color]
            const IconComponent = column.icon

            return (
              <motion.div
                key={column.id}
                className="relative group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                {/* Header Image */}
                <div className="relative h-56 overflow-hidden rounded-t-3xl">
                  <img
                    src={column.image}
                    alt={column.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${colors.overlay}`} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <motion.div
                      className={`w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-3`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <IconComponent className="w-7 h-7 text-white" />
                    </motion.div>
                    <h3 className="text-3xl font-heading font-bold text-white tracking-wider">
                      {column.title}
                    </h3>
                  </div>
                </div>

                {/* Content Section */}
                <div className={`${colors.light} p-8 rounded-b-3xl border-t-4 ${colors.bg}`}>
                  {column.sections.map((section, sIdx) => (
                    <div key={sIdx} className={sIdx > 0 ? 'mt-8 pt-8 border-t border-gray-200' : ''}>
                      {/* Section Title with Underline */}
                      <div className="mb-4">
                        <h4 className="text-lg font-heading font-bold text-dark-100 tracking-wide">
                          {section.title}
                        </h4>
                        <div className={`w-12 h-1 ${colors.underline} mt-2 rounded-full`} />
                      </div>

                      {/* Description */}
                      <p className="text-dark-300 text-sm leading-relaxed mb-5 font-body">
                        {section.description}
                      </p>

                      {/* Button */}
                      <Link to={section.buttonLink}>
                        <motion.button
                          className={`
                            inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-wide
                            rounded-xl transition-all duration-300 shadow-lg
                            ${colors.bg} text-white ${colors.bgHover}
                          `}
                          whileHover={{ scale: 1.02, y: -2 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          {section.buttonText}
                          <ArrowRight className="w-4 h-4" />
                        </motion.button>
                      </Link>
                    </div>
                  ))}
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-dark-300 mb-6 font-body">
            Not sure where to start? We're here to help!
          </p>
          <Link to="/contact">
            <motion.button
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 text-white font-bold rounded-full shadow-xl hover:shadow-2xl transition-all duration-300"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <Users className="w-5 h-5" />
              Contact Us
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  )
}

export default NextSteps
